import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAccount, useWriteContract, useWalletClient, usePublicClient } from 'wagmi';
import { useAllTokens } from '../../hooks/useAllTokens';
import { useCreatorEarnings, formatEarnings } from '../../hooks/useCreatorEarnings';
import { useUnclaimedCreatorFees } from '../../hooks/useUnclaimedCreatorFees';
import { processCreatorEarnings } from '../../utils/transactionParser';
// ProfileWallet removed - portfolio is now in global sidebar
import { mintCreatorCard, isCardMinted } from '../../utils/mintCreatorCard';
import { config } from '../../utils/config';
import { ethers } from 'ethers6';
import { generateAndUploadNFTMetadata } from '../../utils/generateNFTMetadata';
import { supabase } from '../../utils/supabase';
import { useZNSName } from '../../hooks/useZNSName';
import { ZNSAddressDisplay } from '../shared/ZNSAddressDisplay';
import { ZNSRegistrationModal } from '../shared/ZNSRegistrationModal';
// PortfolioOverview removed - now in global sidebar

// TokenItem component for individual token display in Creator Earnings
interface TokenItemProps {
  token: any;
  nftTokenId: string | null;
  isCollecting: boolean;
  status: 'success' | 'error' | null;
  collectEarnings: (token: any) => void;
  earningsStatus: { [tokenId: string]: 'success' | 'error' | null };
  tokenId: string;
  dex?: string; // 'Protofire' or 'InkySwap' - needed for V3 factory fee queries
}

const TokenItemComponent: React.FC<TokenItemProps> = ({
  token,
  nftTokenId,
  isCollecting,
  status,
  collectEarnings,
  tokenId,
  dex
}) => {
  const [showUnclaimedFees, setShowUnclaimedFees] = useState(false);
  const [isMinting, setIsMinting] = useState(false);
  const { data: walletClient } = useWalletClient();
  const { unclaimedFees, isLoading: isLoadingFees, error: feesError } = useUnclaimedCreatorFees(
    showUnclaimedFees ? nftTokenId : null,
    dex
  );

  const handleViewUnclaimedFees = () => {
    setShowUnclaimedFees(!showUnclaimedFees);
  };

  const handleMintCard = async () => {
    if (!walletClient || !token.contract_address || !token.deployer_address) {
      alert('Please connect your wallet to mint your creator card');
      return;
    }

    try {
      setIsMinting(true);

      // Fetch ZNS name for the creator before generating metadata (only for Ethereum addresses)
      let creatorZNSName: string | null = null;
      if (token.deployer_address && token.deployer_address.startsWith('0x') && token.deployer_address.length === 42) {
        try {
          const response = await fetch(
            `https://zns.bio/api/resolveAddress?chain=57073&address=${token.deployer_address.toLowerCase()}`,
            { signal: AbortSignal.timeout(5000) } // 5 second timeout
          );
          if (response.ok) {
            const data = await response.json();
            if (data.code === 200 && data.primaryDomain) {
              creatorZNSName = `${data.primaryDomain}.ink`;
            }
          }
        } catch (znsError) {
          // Silently fail - ZNS lookup is optional
        }
      }

      // Generate and upload NFT metadata JSON
      console.log('🎨 Generating NFT metadata...');
      await generateAndUploadNFTMetadata(token.contract_address, {
        symbol: token.symbol,
        logo_url: token.logo_url || null,
        description: token.description || null,
        deployer_address: token.deployer_address,
        created_at: token.created_at || new Date().toISOString(),
        pool_address: token.pool_address || null,
        nft_token_id: token.nft_token_id || null,
        zns_domain: creatorZNSName  // Include ZNS domain if found
      });

      // Then mint the NFT
      const provider = new ethers.BrowserProvider(window.ethereum);
      const txHash = await mintCreatorCard(provider, {
        tokenAddress: token.contract_address,
        deployerAddress: token.deployer_address,
        edition: 1
      });

      alert(`Card minted successfully! Transaction: ${txHash}`);

      // Update the minted status in the database
      // Use upsert to create the record if it doesn't exist
      const { error } = await supabase
        .from('token_cards')
        .upsert({
          contract_address: token.contract_address,
          minted: true,
          deployer_address: token.deployer_address || undefined,
          card_url: token.card_url || null,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'contract_address',
          ignoreDuplicates: false
        });

      if (error) {
        console.error('Failed to update minted status:', error);
      } else {
        console.log('✅ Minted status updated in database');
      }

    } catch (error: any) {
      console.error('Failed to mint card:', error);
      alert(error.message || 'Failed to mint card. Please try again.');
    } finally {
      setIsMinting(false);
    }
  };

  // Determine button text based on state
  const getButtonText = () => {
    if (isLoadingFees) return 'Loading...';
    if (feesError) return 'Error';
    if (showUnclaimedFees && unclaimedFees) {
      if (unclaimedFees.usdValue > 0) {
        return `$${unclaimedFees.usdValue.toFixed(4)}`;
      }
      return '$0.0000';
    }
    return 'View Fees';
  };

  return (
    <TokenCard key={`token-${token.symbol}-${tokenId}`}>
      <TokenInfo>
        <TokenName>{token.symbol}</TokenName>
        <TokenButtonsRow>
          <button
            onClick={handleViewUnclaimedFees}
            style={{
              background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 12px',
              color: '#ffffff',
              fontSize: '11px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              flex: '1 1 auto',
              minWidth: '70px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #a855f7 0%, #8b5cf6 100%)';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(147, 51, 234, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {getButtonText()}
          </button>
          {nftTokenId && (
            <CollectEarningsButton
              onClick={() => collectEarnings(token)}
              disabled={isCollecting}
            >
              {isCollecting ? 'Collecting...' : status === 'success' ? '✓ Collected' : 'Collect'}
            </CollectEarningsButton>
          )}
          {token.minted !== true && (
            <CollectEarningsButton
              onClick={handleMintCard}
              disabled={isMinting}
              style={{
                background: isMinting ? '#6b7280' : 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
              }}
            >
              {isMinting ? 'Minting...' : 'Mint Card'}
            </CollectEarningsButton>
          )}
        </TokenButtonsRow>
      </TokenInfo>
    </TokenCard>
  );
};

const WalletPanelContainer = styled.div`
  background: transparent;
  border-radius: 16px;
  padding: 32px 24px;
  margin: 0 auto;
  width: 100%;
  position: relative;
  z-index: 10;
  backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  gap: 24px;
  
  /* Responsive grid for creator info */
  .creator-info-grid {
    @media (max-width: 768px) {
      grid-template-columns: 1fr !important;
    }
  }
`;

const ProfileTitle = styled.h1`
  font-family: 'Inter', sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #fffeff;
  margin: 0 0 24px 0;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 0 16px;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 20px;
    gap: 6px;
    padding: 0 8px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    gap: 4px;
  }
`;

const ProfileZNSIcon = styled.img`
  width: 32px;
  height: 32px;
  display: inline-block;
  vertical-align: middle;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
  }

  @media (max-width: 480px) {
    width: 20px;
    height: 20px;
  }
`;

const ProfileZNSName = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #fffeff;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;

  @media (max-width: 768px) {
    font-size: 20px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    max-width: calc(100vw - 120px);
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #888888;
  text-align: center;
  gap: 16px;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

// Removed unused styled components

// Removed ProfileGrid - no longer needed with unified dashboard

// Individual container styling to match token cards
const ProfileContainer = styled.div`
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  backdrop-filter: blur(8px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: rgba(0, 0, 0, 0.6);
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  }
`;

const ContainerTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  font-family: 'Inter', sans-serif;
`;

const CreatorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 10px;
  padding: 14px;
`;

const InfoLabel = styled.span`
  color: #9ca3af;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.span`
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  word-break: break-all;
`;

const CreateTokenLink = styled.button`
  background: transparent;
  border: none;
  color: #a855f7;
  font-size: 14px;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  transition: all 0.2s ease;
  
  &:hover {
    color: #9333ea;
    text-decoration: none;
  }
`;

// Removed TopTokensList, TopTokenItem, TopTokenName, FavoriteCount - no longer needed

const TokenCard = styled.div`
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 8px;
`;

const TokenInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TokenButtonsRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 6px;
  }
`;

const TokenName = styled.div`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
`;

const TokenBalance = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #9ca3af;
`;

// Removed unused TokenGrid

const CollectEarningsButton = styled.button`
  background: linear-gradient(135deg, #9333ea 0%, #7c3aed 100%);
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex: 1 1 auto;
  min-width: 70px;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #a855f7 0%, #8b5cf6 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(147, 51, 234, 0.3);
  }
  
  &:disabled {
    background: #6b7280;
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  @media (max-width: 480px) {
    width: 100%;
    padding: 8px 12px;
    font-size: 12px;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  color: #888888;
`;

// Removed unused EmptyMessage

const WalletPanel: React.FC = () => {
  const navigate = useNavigate();
  const { isConnected, address } = useAccount();
  const { allTokens } = useAllTokens();
  const { writeContract, data: txHash } = useWriteContract();
  const publicClient = usePublicClient();

  const displayAddress = address;
  const { earningsData, ethPrice, refetch: refetchEarnings } = useCreatorEarnings(displayAddress);
  const { znsName } = useZNSName(address);

  const [creatorStats, setCreatorStats] = useState<{
    tokensCreated: number;
    mostPopularToken: any;
    memberSince: string;
    recentTokens: any[];
    allTokens: any[]; // Add all tokens for expandable view
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [collectingEarnings, setCollectingEarnings] = useState<{ [tokenId: string]: boolean }>({});
  const [earningsStatus, setEarningsStatus] = useState<{ [tokenId: string]: 'success' | 'error' | null }>({});
  const [currentCollectingTokenId, setCurrentCollectingTokenId] = useState<string | null>(null);
  const [showAllTokens, setShowAllTokens] = useState(false); // State for expandable token list
  const [showZNSModal, setShowZNSModal] = useState(false); // State for ZNS registration modal

  // Debug logging
  useEffect(() => {
    console.log('🔍 [WalletPanel] Connected Address:', address);
    console.log('🔍 [WalletPanel] Display Address:', displayAddress);
    console.log('🔍 [WalletPanel] Earnings data:', earningsData);
    console.log('🔍 [WalletPanel] ETH price:', ethPrice);
    console.log('🔍 [WalletPanel] All tokens count:', allTokens.length);
    console.log('🔍 [WalletPanel] All tokens sample:', allTokens.slice(0, 3).map(t => ({
      name: t.name,
      deployer: t.deployer_address,
      contract: t.contract_address
    })));
  }, [address, displayAddress, earningsData, ethPrice, allTokens]);

  // Monitor transaction hash and process when available
  useEffect(() => {
    if (txHash && address && currentCollectingTokenId) {
      console.log(`✅ [CollectEarnings] Transaction hash received: ${txHash}`);

      // Wait a moment for the transaction to be mined, then process the receipt
      setTimeout(async () => {
        try {
          console.log(`🔍 [CollectEarnings] Processing transaction receipt for hash: ${txHash}`);
          const { success, error: parseError } = await processCreatorEarnings(
            txHash,
            address, // Creator address
            currentCollectingTokenId
          );

          if (success) {
            console.log(`✅ [CollectEarnings] Supabase updated for transaction: ${txHash}`);
            // Set success status and reset collecting state
            setEarningsStatus(prev => ({ ...prev, [currentCollectingTokenId]: 'success' }));
            setCollectingEarnings(prev => ({ ...prev, [currentCollectingTokenId]: false }));
            // Refetch earnings data to update the UI
            refetchEarnings();
            setCurrentCollectingTokenId(null);
          } else {
            console.error(`❌ [CollectEarnings] Error parsing transaction or updating Supabase: ${parseError}`);
            setEarningsStatus(prev => ({ ...prev, [currentCollectingTokenId]: 'error' }));
            setCollectingEarnings(prev => ({ ...prev, [currentCollectingTokenId]: false }));
            setCurrentCollectingTokenId(null);
          }
        } catch (parseError) {
          console.error(`❌ [CollectEarnings] Error processing transaction: ${parseError}`);
          setEarningsStatus(prev => ({ ...prev, [currentCollectingTokenId]: 'error' }));
          setCollectingEarnings(prev => ({ ...prev, [currentCollectingTokenId]: false }));
          setCurrentCollectingTokenId(null);
        }
      }, 3000); // Wait 3 seconds for transaction to be mined
    }
  }, [txHash, address, refetchEarnings, currentCollectingTokenId]);

  // Locker contract configuration - Support V3 Factory + legacy V1/V2 lockers
  const FACTORY_V3_ADDRESS = '0xE01118dbcF8Ba2609d3ffBD6A53d9533c7526D60'; // V3 Factory (self-custody LP)
  const LOCKER_V2_ADDRESS = '0x89264dBC5CDc22BbB00ecc672C717D7205647702'; // Legacy V2 (Multi-DEX)
  const LOCKER_V1_ADDRESS = '0x0f417FaB5b78aaCC265137Ebd83b41c44f515d3F'; // Legacy V1
  
  // NPM addresses for fee collection (hardcoded)
  const PROTOFIRE_NPM = '0xC0836E5B058BBE22ae2266e1AC488A1A0fD8DCE8';  // Protofire (aka Reservoir)
  const INKYSWAP_NPM = '0x1921C0c86494d25F90E9a535327ec989372a860f';   // InkySwap

  // V3 Factory ABI for collectFees
  const FACTORY_V3_ABI = [
    {
      "inputs": [
        { "internalType": "uint256", "name": "tokenId", "type": "uint256" },
        { "internalType": "address", "name": "npm", "type": "address" }
      ],
      "name": "collectFees",
      "outputs": [],
      "stateMutability": "nonpayable",
      "type": "function"
    },
    {
      "inputs": [{ "internalType": "uint256", "name": "tokenId", "type": "uint256" }],
      "name": "nftCreators",
      "outputs": [{ "internalType": "address", "name": "", "type": "address" }],
      "stateMutability": "view",
      "type": "function"
    }
  ];

  // Legacy locker ABI
  const LOCKER_ABI = [
    {
      "inputs": [
        {
          "internalType": "uint256",
          "name": "tokenId",
          "type": "uint256"
        }
      ],
      "name": "collectFeesAsCreator",
      "outputs": [],
      "stateMutability": "nonpayable",
      "type": "function"
    }
  ];

  // Function to collect earnings for a specific token
  // Tries V3 factory first (self-custody LP), then falls back to legacy lockers
  const collectEarnings = async (token: any) => {
    if (!token.nft_token_id) {
      alert('No NFT token ID found for this token. Cannot collect earnings.');
      return;
    }

    const tokenId = token.nft_token_id;

    setCollectingEarnings(prev => ({ ...prev, [tokenId]: true }));
    setEarningsStatus(prev => ({ ...prev, [tokenId]: null }));
    setCurrentCollectingTokenId(tokenId);

    try {
      // Determine which NPM to use based on the token's DEX
      // InkySwap uses InkySwap NPM, Protofire (default) uses Protofire NPM
      const npmAddress = token.dex === 'InkySwap' ? INKYSWAP_NPM : PROTOFIRE_NPM;
      console.log(`🔍 [CollectEarnings] Token DEX: ${token.dex || 'Protofire (default)'}, using NPM: ${npmAddress}`);

      // Determine where the LP NFT is held: V3 factory or legacy lockers
      let contractAddress = FACTORY_V3_ADDRESS;
      let contractAbi: any = FACTORY_V3_ABI;
      let functionName = 'collectFees';
      let args: any[] = [BigInt(tokenId), npmAddress as `0x${string}`];

      if (publicClient && address) {
        // First, try V3 factory (check if this NFT is in the factory)
        try {
          // Check if the factory holds this NFT (nftCreators mapping returns creator address)
          const creator = await publicClient.readContract({
            address: FACTORY_V3_ADDRESS as `0x${string}`,
            abi: FACTORY_V3_ABI,
            functionName: 'nftCreators',
            args: [BigInt(tokenId)]
          });
          
          if (creator && creator !== '0x0000000000000000000000000000000000000000') {
            // NFT is in V3 factory - try simulation with correct NPM based on DEX
            await publicClient.simulateContract({
              address: FACTORY_V3_ADDRESS as `0x${string}`,
              abi: FACTORY_V3_ABI,
              functionName: 'collectFees',
              args: [BigInt(tokenId), npmAddress as `0x${string}`],
              account: address
            });
            console.log(`✅ [CollectEarnings] V3 factory simulation successful for tokenId: ${tokenId}, NPM: ${npmAddress}`);
          } else {
            // Not in V3 factory, try legacy lockers
            throw new Error('NFT not in V3 factory');
          }
        } catch (v3Error: any) {
          console.log(`⚠️ [CollectEarnings] V3 factory not holding NFT ${tokenId}, trying legacy lockers...`);
          
          // Try V2 locker
          try {
            await publicClient.simulateContract({
              address: LOCKER_V2_ADDRESS as `0x${string}`,
              abi: LOCKER_ABI,
              functionName: 'collectFeesAsCreator',
              args: [BigInt(tokenId)],
              account: address
            });
            contractAddress = LOCKER_V2_ADDRESS;
            contractAbi = LOCKER_ABI;
            functionName = 'collectFeesAsCreator';
            args = [BigInt(tokenId)];
            console.log(`✅ [CollectEarnings] V2 locker simulation successful for tokenId: ${tokenId}`);
          } catch (v2Error: any) {
            // V2 failed, try V1
            const errorMessage = v2Error?.message || v2Error?.toString() || '';
            if (errorMessage.includes('LP not locked') ||
              errorMessage.includes('not locked') ||
              errorMessage.includes('revert')) {
              try {
                await publicClient.simulateContract({
                  address: LOCKER_V1_ADDRESS as `0x${string}`,
                  abi: LOCKER_ABI,
                  functionName: 'collectFeesAsCreator',
                  args: [BigInt(tokenId)],
                  account: address
                });
                contractAddress = LOCKER_V1_ADDRESS;
                contractAbi = LOCKER_ABI;
                functionName = 'collectFeesAsCreator';
                args = [BigInt(tokenId)];
                console.log(`✅ [CollectEarnings] V1 locker simulation successful for tokenId: ${tokenId}`);
              } catch (v1Error) {
                console.error('❌ [CollectEarnings] All sources failed simulation:', v1Error);
                throw new Error('Token not found in V3 factory or legacy lockers. It may not be locked or you may not be the creator.');
              }
            } else {
              throw v2Error;
            }
          }
        }
      }

      // Execute the transaction with the determined contract
      const sourceLabel = contractAddress === FACTORY_V3_ADDRESS ? 'V3 Factory' : 
                          contractAddress === LOCKER_V2_ADDRESS ? 'V2 Locker' : 'V1 Locker';
      console.log(`🔄 [CollectEarnings] Collecting fees from ${sourceLabel} for tokenId: ${tokenId}`);
      
      writeContract({
        address: contractAddress as `0x${string}`,
        abi: contractAbi,
        functionName: functionName,
        args: args,
        gasPrice: BigInt('200000000') // 0.0002 GWEI = 200000000 wei
      });

    } catch (error: any) {
      console.error('❌ [CollectEarnings] Error collecting earnings:', error);
      setEarningsStatus(prev => ({ ...prev, [tokenId]: 'error' }));
      setCurrentCollectingTokenId(null);
      setCollectingEarnings(prev => ({ ...prev, [tokenId]: false }));

      // Show user-friendly error message
      const errorMessage = error?.message || error?.toString() || 'Unknown error';
      if (errorMessage.includes('not found') || errorMessage.includes('not locked')) {
        alert('This token\'s LP NFT was not found in any locker. You may not be the creator.');
      } else if (errorMessage.includes('User rejected')) {
        // User rejected, don't show error
      } else {
        alert(`Failed to collect earnings: ${errorMessage}`);
      }

      // Reset status after 5 seconds
      setTimeout(() => {
        setEarningsStatus(prev => ({ ...prev, [tokenId]: null }));
      }, 5000);
    }
  };

  // Fetch creator stats from Supabase (no RPC calls)
  useEffect(() => {
    const fetchCreatorStats = async () => {
      console.log('🔍 [CreatorStats] Starting fetch with:', {
        isConnected,
        address,
        displayAddress,
        allTokensCount: allTokens.length
      });

      // Show stats if wallet is connected
      if (!isConnected || !address) {
        console.log('🔍 [CreatorStats] No connection, clearing stats');
        setCreatorStats(null);
        return;
      }

      setLoading(true);
      try {
        console.log('🔍 [CreatorStats] Filtering tokens for address:', displayAddress);
        console.log('🔍 [CreatorStats] Sample deployer addresses from all tokens:',
          allTokens.slice(0, 5).map(t => ({
            name: t.name,
            deployer: t.deployer_address,
            deployerLower: t.deployer_address?.toLowerCase(),
            displayAddressLower: displayAddress?.toLowerCase()
          }))
        );

        // Get tokens created by the display address (custodial or connected wallet)
        const userTokens = allTokens.filter(token =>
          token.deployer_address?.toLowerCase() === displayAddress?.toLowerCase()
        );

        console.log('🔍 [CreatorStats] Found user tokens:', {
          userTokensCount: userTokens.length,
          userTokens: userTokens.map(t => ({
            name: t.name,
            symbol: t.symbol,
            deployer: t.deployer_address,
            contract: t.contract_address
          })),
          showAllTokensButton: userTokens.length > 5 ? 'WILL SHOW' : 'WILL NOT SHOW'
        });

        // Find most popular token (highest favorites)
        const mostPopular = userTokens.reduce((prev, current) =>
          (current.favorites_count || 0) > (prev?.favorites_count || 0) ? current : prev
          , userTokens[0] || null);

        // Get recent tokens (last 5)
        const recentTokens = userTokens
          .sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime())
          .slice(0, 5);

        // Get member since date (oldest token creation)
        const oldestToken = userTokens
          .sort((a, b) => new Date(a.created_at || '').getTime() - new Date(b.created_at || '').getTime())[0];

        const stats = {
          tokensCreated: userTokens.length,
          mostPopularToken: mostPopular,
          memberSince: oldestToken?.created_at ? new Date(oldestToken.created_at).toLocaleDateString() : 'Unknown',
          recentTokens: recentTokens,
          allTokens: userTokens // Store all tokens for expandable view
        };

        console.log('🔍 [CreatorStats] Setting final stats:', stats);
        setCreatorStats(stats);
      } catch (error) {
        console.error('❌ [CreatorStats] Error fetching creator stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCreatorStats();
  }, [isConnected, address, displayAddress, allTokens]);

  return (
    <WalletPanelContainer>
      <ProfileTitle>
        {znsName ? (
          <>
            <ProfileZNSIcon src="/zns_icon_purple.png" alt="ZNS" />
            <ProfileZNSName>{znsName}</ProfileZNSName>
            <span>Profile</span>
          </>
        ) : (
          'Creator Profile'
        )}
      </ProfileTitle>

      {/* Portfolio Overview removed - now in global sidebar */}

      {!isConnected || !address ? (
        <EmptyState>
          <EmptyIcon>🔗</EmptyIcon>
          <div>Connect your wallet to view your creator profile</div>
          <div style={{ fontSize: '12px', marginTop: '8px' }}>
            Your token creation history will appear here once connected
          </div>
        </EmptyState>
      ) : (
        <>
          {/* Unified Creator Dashboard - All creator info in one place */}
          {loading ? (
            <LoadingState>Loading creator stats...</LoadingState>
          ) : creatorStats ? (
            <>
              <ProfileContainer style={{ maxWidth: '100%' }}>
                <ContainerTitle>CREATOR DASHBOARD</ContainerTitle>

                {/* Creator Info Grid - 2 columns */}
                <CreatorInfo style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '12px',
                  marginBottom: '20px'
                }}
                  className="creator-info-grid"
                >
                  <InfoRow>
                    <InfoLabel>Creator Address</InfoLabel>
                    <InfoValue>{displayAddress}</InfoValue>
                  </InfoRow>
                  {znsName ? (
                    <InfoRow>
                      <InfoLabel>ZNS Domain</InfoLabel>
                      <InfoValue>
                        <ZNSAddressDisplay address={address || ''} showIcon={true} truncateLength={20} />
                      </InfoValue>
                    </InfoRow>
                  ) : (
                    <InfoRow style={{
                      background: '#000000',
                      border: '1px solid #333333',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#333333';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#000000';
                      }}
                      onClick={() => setShowZNSModal(true)}
                    >
                      <InfoLabel>🌐 Web3 Identity</InfoLabel>
                      <InfoValue style={{
                        color: '#a855f7',
                        fontWeight: '600',
                        fontSize: '13px'
                      }}>
                        Register your Web3 Identity with ZNS Connect →
                      </InfoValue>
                    </InfoRow>
                  )}
                  <InfoRow>
                    <InfoLabel>Tokens Deployed</InfoLabel>
                    <InfoValue>{creatorStats.tokensCreated}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>First Creation Date</InfoLabel>
                    {creatorStats.tokensCreated > 0 ? (
                      <InfoValue>{creatorStats.memberSince}</InfoValue>
                    ) : (
                      <CreateTokenLink onClick={() => navigate('/create')}>
                        Create your first token!
                      </CreateTokenLink>
                    )}
                  </InfoRow>
                  {earningsData && (
                    <>
                      <InfoRow>
                        <InfoLabel>Total WETH Earned</InfoLabel>
                        <InfoValue>{formatEarnings(earningsData.totalWethEarned)}</InfoValue>
                      </InfoRow>
                      <InfoRow>
                        <InfoLabel>Earnings USD Value</InfoLabel>
                        <InfoValue style={{ color: '#10b981', fontWeight: 700 }}>
                          {ethPrice && earningsData.totalWethEarned
                            ? `$${(parseFloat(earningsData.totalWethEarned) * ethPrice).toFixed(2)}`
                            : 'Loading...'
                          }
                        </InfoValue>
                      </InfoRow>
                      <InfoRow>
                        <InfoLabel>Total Collections</InfoLabel>
                        <InfoValue>{earningsData.totalCollections}</InfoValue>
                      </InfoRow>
                    </>
                  )}
                </CreatorInfo>

                {/* Creator Earnings Token List */}
                <div style={{
                  borderTop: '1px solid #333333',
                  paddingTop: '20px',
                  marginTop: '20px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <ContainerTitle style={{ margin: 0 }}>Token Earnings</ContainerTitle>
                    {creatorStats.allTokens.length > 5 && (
                      <button
                        onClick={() => setShowAllTokens(!showAllTokens)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#a855f7',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          transition: 'all 0.2s ease',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#333333';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        {showAllTokens ? '▲ Show Less' : `▼ View All (${creatorStats.allTokens.length})`}
                      </button>
                    )}
                  </div>
                  {creatorStats.recentTokens.length > 0 ? (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      maxHeight: showAllTokens ? '400px' : 'none',
                      overflowY: showAllTokens ? 'auto' : 'visible',
                      paddingRight: showAllTokens ? '8px' : '0'
                    }}>
                      {(
                        showAllTokens
                          ? creatorStats.allTokens
                          : creatorStats.recentTokens.slice(0, 3)
                      ).map((token, index) => {
                        const tokenId = token.nft_token_id || `${token.symbol}-${index}`;
                        const isCollecting = collectingEarnings[tokenId];
                        const status = earningsStatus[tokenId];

                        return (
                          <TokenItemComponent
                            key={`${token.symbol}-${index}`}
                            nftTokenId={token.nft_token_id}
                            isCollecting={isCollecting}
                            status={status}
                            token={token}
                            collectEarnings={collectEarnings}
                            earningsStatus={earningsStatus}
                            tokenId={tokenId}
                            dex={token.dex}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{
                      color: '#9ca3af',
                      fontSize: '14px',
                      textAlign: 'center',
                      padding: '20px 0'
                    }}>
                      No tokens to collect earnings from
                    </div>
                  )}
                </div>
              </ProfileContainer>
              {/* Wallet/Portfolio removed - now in global sidebar */}
            </>
          ) : (
            <div style={{
              color: '#9ca3af',
              textAlign: 'center',
              padding: '20px',
              fontSize: '14px'
            }}>
              Create your first token to unlock creator stats!
            </div>
          )}
        </>
      )}

      {/* ZNS Registration Modal */}
      <ZNSRegistrationModal
        isVisible={showZNSModal}
        onClose={() => setShowZNSModal(false)}
      />
    </WalletPanelContainer>
  );
};

export default WalletPanel;
